import { forwardRef, useEffect, useImperativeHandle } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { format } from "date-fns";
import {
  genTemplateBodyDate,
  genTemplateBodyNumber,
  genTemplateBodyText,
} from "@components/utils/TableTemplateBodyGenerator";
import useIsMobile from "../../../hooks/useIsMobile";
import { useAPIGetPermintaanMinyak } from "../api/api-get-permintaan-minyak";
import { getProgressText, getStatusInfo } from "../utils/permintaan-minyak-helpers";
import PermintaanMinyakFilter from "./permintaan-minyak-filter";

const PermintaanMinyakList = forwardRef((props, ref) => {
  const isMobile = useIsMobile();
  const { onClickRowDelete, onClickRowEdit, onClickAddNew } = props;

  const {
    loading,
    listData,
    fetchListData,
    filterValue,
    setFilterValue,
    handleOnClickRefresh,
    handleOnPage,
    paging,
    handleOnUpdateViewRow,
    handleOnDeleteViewRow,
  } = useAPIGetPermintaanMinyak();

  useImperativeHandle(ref, () => ({
    handleOnUpdateViewRow: (row) => {
      handleOnUpdateViewRow(row);
    },
    handleOnDeleteViewRow: (row) => {
      handleOnDeleteViewRow(row);
    },
  }));

  // use effect section

  useEffect(() => {
    fetchListData({ page: paging.page, take: paging.take });
  }, []);

  // only a draft that is not signed yet can be deleted
  const canDelete = (row) => (row.status ?? 0) === 0 && !row.verif_diajukan_oleh_id;

  //  custom ui section

  const nomorBodyTemplate = (row) =>
    genTemplateBodyText({ row, field: "nomor" });

  const tanggalBodyTemplate = (row) =>
    genTemplateBodyDate({ row, field: "tanggal" });

  const departmentBodyTemplate = (row) =>
    genTemplateBodyText({ row, field: "data_department.nama" });

  const jenisMinyakBodyTemplate = (row) =>
    genTemplateBodyText({ row, field: "data_jenis_minyak.nama" });

  const diminta = (row) =>
    genTemplateBodyNumber({ row, field: "jumlah_diminta", maxFraction: 2 });

  const disetujui = (row) =>
    genTemplateBodyNumber({ row, field: "jumlah_disetujui", maxFraction: 2 });

  const diterima = (row) =>
    genTemplateBodyNumber({ row, field: "jumlah_diterima", maxFraction: 2 });

  const statusBodyTemplate = (row) => {
    const info = getStatusInfo(row.status);
    return (
      <div className="row-center-center">
        <Tag value={info.label} severity={info.severity} />
      </div>
    );
  };

  const actionBodyTemplate = (row) => {
    return (
      <div className={`row-center-center gap-2 ${isMobile ? "pt-4" : ""}`}>
        <Button
          icon="pi pi-file-edit"
          rounded
          className="bg-indigo-400 max-w-[1.85rem] max-h-[1.85rem]"
          onClick={() => {
            if (onClickRowEdit) onClickRowEdit(row);
          }}
          pt={{ icon: { className: "!text-xs" } }}
        />
        <Button
          icon="pi pi-trash"
          rounded
          severity="danger"
          disabled={!canDelete(row)}
          className="bg-red-400 max-w-[1.85rem] max-h-[1.85rem]"
          onClick={() => {
            if (onClickRowDelete) onClickRowDelete(row);
          }}
          pt={{ icon: { className: "!text-xs" } }}
        />
      </div>
    );
  };

  const mobileBodyTemplate = (row) => {
    const info = getStatusInfo(row.status);

    return (
      <div className="col-start-start pb-1 w-full">
        <div className="row-between-start w-full">
          <span className="font-bold text-indigo-500 text-sm">{row.nomor}</span>
          <span className="font-semibold text-slate-400 text-sm italic">
            {row.tanggal ? format(new Date(row.tanggal), "dd/MM/yyyy") : ""}
          </span>
        </div>
        <div className="row-between-center w-full pt-2">
          <span className="font-bold text-slate-500 text-sm">
            {row.data_department?.nama} | {row.data_jenis_minyak?.nama}
          </span>
          <Tag value={info.label} severity={info.severity} />
        </div>
        <div className="row-start-center w-full gap-2 pt-2">
          <i className="pi pi-gauge text-slate-400" />
          <span className="text-slate-500 text-sm">
            Diminta <strong>{diminta(row)} L</strong>
            {row.jumlah_disetujui > 0 && (
              <>
                {" · "}Disetujui <strong>{disetujui(row)} L</strong>
              </>
            )}
            {row.jumlah_diterima > 0 && (
              <>
                {" · "}Diterima <strong>{diterima(row)} L</strong>
              </>
            )}
          </span>
        </div>
        <div className="row-start-center w-full gap-2 pt-2">
          <i className="pi pi-list-check text-slate-400" />
          <span className="text-slate-500 text-sm">{getProgressText(row)}</span>
        </div>
        <div className="row-between-center w-full gap-2 pt-2">
          <Button
            icon="pi pi-file-edit"
            label="Buka"
            rounded
            className="bg-indigo-400 max-h-[2rem] !px-4"
            onClick={() => {
              if (onClickRowEdit) onClickRowEdit(row);
            }}
            pt={{ icon: { className: "!text-xs" } }}
          />
          <Button
            icon="pi pi-trash"
            rounded
            severity="danger"
            disabled={!canDelete(row)}
            className="bg-red-400 max-w-[2rem] max-h-[2rem]"
            onClick={() => {
              if (onClickRowDelete) onClickRowDelete(row);
            }}
            pt={{ icon: { className: "!text-xs" } }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="col-start-start w-full gap-4">
      {/* filter section */}
      <div className="elevated-card-tight-vertical row-start-start w-full gap-4">
        <PermintaanMinyakFilter
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          onClickRefresh={handleOnClickRefresh}
        />
      </div>

      {/* table section */}
      <div
        className={`elevated-card-tight-vertical ${
          isMobile ? "col-start-center" : "col-start-end"
        } w-full gap-2`}
      >
        <div className="row-start-center w-full">
          <Button
            icon="pi pi-plus"
            label="Buat Permintaan"
            onClick={() => {
              if (onClickAddNew) onClickAddNew();
            }}
          />
        </div>

        {!isMobile && (
          <DataTable
            dataKey="id"
            className="w-100"
            loading={loading}
            showGridlines
            stripedRows
            value={listData}
          >
            <Column
              field="no"
              header="#"
              className="text-center"
              headerClassName="w-[4rem]"
              alignHeader={"center"}
            />
            <Column
              header="Action"
              alignHeader={"center"}
              headerClassName="w-[8rem]"
              body={actionBodyTemplate}
            />
            <Column header="Nomor" body={nomorBodyTemplate} />
            <Column header="Tanggal" body={tanggalBodyTemplate} />
            <Column header="Department" body={departmentBodyTemplate} />
            <Column header="Jenis Minyak" body={jenisMinyakBodyTemplate} />
            <Column align={"right"} header="Diminta (L)" body={diminta} />
            <Column align={"right"} header="Disetujui (L)" body={disetujui} />
            <Column align={"right"} header="Diterima (L)" body={diterima} />
            <Column header="Status" align={"center"} body={statusBodyTemplate} />
          </DataTable>
        )}
        {isMobile && (
          <DataTable
            dataKey="id"
            className="w-100"
            loading={loading}
            showGridlines
            stripedRows
            value={listData}
          >
            <Column header="Permintaan Minyak" body={mobileBodyTemplate} />
          </DataTable>
        )}

        <Paginator
          first={parseInt(paging.take) * (paging.page - 1)}
          rows={parseInt(paging.take)}
          totalRecords={parseInt(paging.total)}
          rowsPerPageOptions={[5, 10, 20, 50]}
          template={
            isMobile
              ? { layout: "PrevPageLink CurrentPageReport NextPageLink" }
              : undefined
          }
          onPageChange={handleOnPage}
        />
      </div>
    </div>
  );
});

export default PermintaanMinyakList;
